import { PrimeMark } from '@/components/PrimeMark'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  Bell,
  ChevronRight,
  Database,
  EyeOff,
  Info,
  UserRound,
  Layers,
  LayoutGrid,
  SunMoon,
  Mic,
  Plug,
  Server,
  Share2,
  ShieldCheck,
  BarChart3,
  Music,
  AudioLines,
  HeartPulse,
  CalendarDays,
  CalendarHeart,
  KeyRound,
} from 'lucide-react'
import { api, db, engine, mutate } from '@/runtime'
import { useMeta, useSettings } from '@/db/hooks'
import { META } from '@/db/schema'
import { HomeSettings } from '@/os/HomeSettings'
import { AppearanceSettings } from '@/components/AppearanceSettings'
import { AGENT_DAILY_USD, type SettingsSpec } from '@/kinds/settings'
import { patchSettings } from '@/lib/settingsWrite'
import { subscribePush, unsubscribePush, pushSupported } from '@/push'
import { APP_VERSION, applySwUpdate, checkForUpdate, compareServerVersion, onSwUpdate } from '@/sw-register'
import { useGrants, usePeople } from '@/db/hooks'
import { memberNameWith } from '@/lib/people'
import { hashSecret, isLive, memberScope, newSecret, scopeTarget, type GrantResource } from '@/kinds/grant'
import { nameOf, personFor } from '@/lib/people'
import { ShareSheet } from '@/components/ShareSheet'
import { GrantSheet } from '@/components/GrantSheet'
import { MemberAccount } from '@/components/MemberAccount'
import { MemberSheet } from '@/components/MemberSheet'
import { useAccess } from '@/state/access'
import { TokenSheet, describeToken } from '@/components/TokenSheet'
import { Button, Field, PageTitle, Tag, cx, SectionTitle, Segmented } from '@/components/ui'
import { SwitchRow } from '@/components/Switch'
import { Phrases } from '@/components/Phrases'
import { labelWords } from '@/lib/words'
import { useAllTasks, useWorkspaces } from '@/db/hooks'
import { WorkspaceForm } from '@/kinds/workspace/components'
import { DEFAULT_WORKSPACE_ID, DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { SuiteInstallSheet } from '@/components/SuiteInstallSheet'
import { suiteOf } from '@/suites'
import { savePhoneFor } from '@/os/phone'
import { appIdsFor } from '@/lib/useNav'
import { useScope } from '@/state/scope'
import type { WorkspaceResource } from '@/kinds/workspace/apply'
import { ColourPicker } from '@/components/ColourPicker'
import { SecurityPanel } from '@/components/SecurityPanel'
import { VoicePicker } from '@/components/VoicePicker'
import { MemoryList } from '@/components/MemoryList'
import { UsagePanel } from '@/components/UsagePanel'
import { FeatureUsage } from '@/components/FeatureUsage'
import { useVoicePrefs } from '@/state/voicePrefs'
import { todayLocal } from '@/lib/time'
import { hiddenLabel } from '@/lib/follow'
import { toCsv } from '@/lib/wellbeing'
import { CATEGORY_LABEL } from '@/lib/dates'
import { DATE_CATEGORIES, DEFAULT_LEADS } from '@/kinds/date/schema'
import { CATEGORY_ICON } from '@/components/DateBits'
import type { DayResource } from '@/kinds/day/apply'
import { Icon } from '@/lib/icons'
import { IconButtonField } from '@/components/EmojiPicker'
import { labelStyle, tokens, type Hue } from '@/lib/colour'
import { DEFAULT_APP_ICON, ICON_BGS, ICON_STYLES, renderAppIcon, type AppIcon } from '@/lib/appIcon'
import { useMemo } from 'react'
import { useConnectorConfigs } from '@/db/hooks'
import { CONNECTOR_IDS, type ConnectorSpec, type ConnectorStatus } from '@/kinds/connector'
import type { Resource, SpotifyStatus } from '@/db/types'
import { KindError } from '@/kinds/kind'

const ServerSection = ({
  token,
  deviceName,
  deviceId,
}: {
  token: string
  deviceName: string
  deviceId: string
}) => {
  const [tokenDraft, setTokenDraft] = useState(token)
  const [showToken, setShowToken] = useState(false)
  const [nameDraft, setNameDraft] = useState(deviceName)
  const saveToken = async () => {
    await db.setMeta(META.token, tokenDraft.trim())
    await db.setMeta(META.deviceName, nameDraft.trim())
    engine.resume()
    if (deviceId) api.registerDevice({ id: deviceId, name: nameDraft.trim() }).catch(() => {})
  }
  return (
    <section className="mb-6">
      <Field label="Token" hint="Anyone with this can read and write everything; keep it out of screenshots.">
        <div className="flex gap-2">
          <input
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            className="w-full font-mono"
            autoComplete="off"
            type={showToken ? 'text' : 'password'}
            data-testid="token-field"
          />
          <Button
            onClick={() => setShowToken((v) => !v)}
            aria-label={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? 'Hide' : 'Show'}
          </Button>
        </div>
      </Field>
      <Field label="This device">
        <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} className="w-full" />
      </Field>
      <Button variant="primary" onClick={() => void saveToken()} disabled={tokenDraft.trim().length < 32}>
        Save
      </Button>
    </section>
  )
}

/**
 * Settings → Suites: every suite (a workspace in the data model), what it was
 * installed from, Edit (with its Home put back as the manifest has it),
 * Install and Remove (archive; the default suite stays).
 */
const SuitesSection = () => {
  const all = useWorkspaces()
  const suites = all.filter((w) => !w.status.archived_at).sort((a, b) => a.spec.order - b.spec.order)
  const removed = all.filter((w) => w.status.archived_at)
  const [editing, setEditing] = useState<WorkspaceResource | null>(null)
  const [installing, setInstalling] = useState(false)
  const scope = useScope((s) => s.ws)
  const setScope = useScope((s) => s.setWs)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const resetHome = (w: WorkspaceResource) => {
    const m = suiteOf(w)
    if (!confirm(`Put ${w.spec.name}'s Home back as ${m.title} has it? Undo brings yours back.`)) return
    void savePhoneFor(w.spec.slug, m.home(appIdsFor({ nav: w.spec.nav, packs: w.spec.packs })))
  }
  const remove = async (w: WorkspaceResource) => {
    if (!confirm(`Remove ${w.spec.name}? Its things stay and it can be restored later.`)) return
    await mutate.action(w.id, 'archive')
    if (scope === w.spec.slug)
      setScope(suites.find((x) => x.id !== w.id)?.spec.slug ?? DEFAULT_WORKSPACE_SLUG)
  }
  return (
    <section className="mb-6" data-testid="suites-section">
      <p className="text-sm text-muted mb-3">
        A suite is one place: its own things, its apps and its own Home screen. Hold the centre button to move
        between them, or pick Everything to see them all at once.
      </p>
      <ul className="group-list mb-3">
        {suites.map((w) => (
          <li
            key={w.id}
            className="flex items-center gap-2 px-3 min-h-12"
            data-testid={`suite-row-${w.spec.slug}`}
          >
            <span className="text-lg w-6 text-center">
              <Icon value={w.spec.icon} size={18} fallback="◉" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block truncate">{w.spec.name}</span>
              {suiteOf(w).title !== w.spec.name && (
                <span className="block text-xs text-faint truncate">
                  Made from the {suiteOf(w).title} pack
                </span>
              )}
            </span>
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ background: tokens(w.spec.colour as Hue).accent }}
            />
            <Button variant="chip" onClick={() => setEditing(w)}>
              Edit
            </Button>
            {w.id !== DEFAULT_WORKSPACE_ID && (
              <Button variant="chip" onClick={() => void remove(w)} aria-label={`Remove ${w.spec.name}`}>
                Remove
              </Button>
            )}
          </li>
        ))}
      </ul>
      <Button variant="primary" onClick={() => setInstalling(true)} data-testid="suite-install-open">
        Install a suite
      </Button>
      <details className="mt-2 text-xs text-faint">
        <summary className="cursor-pointer min-h-8 flex items-center">Each suite as its own app</summary>
        <p className="mb-2">
          Open a suite's address on your phone and add it to the Home Screen for a separate icon that starts
          there.
        </p>
        <ul className="flex flex-col gap-1">
          {suites.map((w) => (
            <li key={w.id} className="flex gap-2">
              <span className="text-muted">{w.spec.name}</span>
              <code className="break-all">
                {origin}/?ws={w.spec.slug}
              </code>
            </li>
          ))}
        </ul>
      </details>
      {removed.length > 0 && (
        <details className="mt-3 text-sm text-faint">
          <summary className="cursor-pointer min-h-8 flex items-center">Removed suites</summary>
          <ul className="mt-1 flex flex-col gap-1">
            {removed.map((w) => (
              <li key={w.id} className="flex items-center justify-between">
                <span>
                  <Icon value={w.spec.icon} /> {w.spec.name}
                </span>
                <Button variant="chip" onClick={() => void mutate.action(w.id, 'restore')}>
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </details>
      )}
      {editing && (
        <WorkspaceForm
          resource={editing}
          onDone={() => setEditing(null)}
          extra={
            <div className="mb-3">
              <Button onClick={() => resetHome(editing)} data-testid="suite-reset-home">
                Reset Home to {suiteOf(editing).title}'s
              </Button>
            </div>
          }
        />
      )}
      <SuiteInstallSheet open={installing} onClose={() => setInstalling(false)} />
    </section>
  )
}

/** One-tap capture from outside the app: an iOS Shortcut (dictation → inbox) or the share sheet. */
const QuickCaptureSection = ({
  token,
  deviceId,
  settings,
  save,
}: {
  token: string
  deviceId: string
  settings: SettingsSpec
  save: (patch: Partial<SettingsSpec>) => Promise<void>
}) => {
  const [copied, setCopied] = useState<string | null>(null)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const curl = `curl -X POST ${origin}/api/capture -H "Authorization: Bearer ${token}" -H "Content-Type: application/json" -d '{"text":"Call the dentist"}'`
  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      setCopied('Could not copy')
    }
  }
  return (
    <section className="mb-6" data-testid="capture-section">
      <p className="text-xs text-faint mb-2">
        Anything posted to <code className="text-muted">/api/capture</code> with your token becomes an inbox
        task, without opening the app. On iPhone, build a Shortcut: Dictate Text → Get Contents of URL (POST,
        JSON body {'{'}"text": Dictated Text{'}'}, header Authorization: Bearer …) and put it on the Home
        Screen or the Action button. Sharing text to AlfieOS from any app opens quick-add with it filled in.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void copy('URL', `${origin}/api/capture`)}>
          {copied === 'URL' ? 'Copied' : 'Copy capture URL'}
        </Button>
        <Button onClick={() => void copy('Header', `Bearer ${token}`)}>
          {copied === 'Header' ? 'Copied' : 'Copy Authorization header'}
        </Button>
        <Button onClick={() => void copy('curl', curl)} title={deviceId ? 'A ready-to-run example' : ''}>
          {copied === 'curl' ? 'Copied' : 'Copy curl example'}
        </Button>
        <Button onClick={() => void copy('partner', `${origin}/api/capture?from=partner`)}>
          {copied === 'partner' ? 'Copied' : 'Copy a tagged URL (?from=partner)'}
        </Button>
        <span className="text-xs text-faint self-center">
          For someone else, make them a capture link under Sharing instead of giving them yours.
        </span>
      </div>
      <SwitchRow
        className="mt-4 mb-3"
        label="Prime suggests where captures go"
        hint="Every capture, yours or sent through a link, waits at the top of the Inbox with what Prime suggests (a day, a project, a list). Nothing changes until you tap Do it; you can also talk it over, keep it as it is, or remove it. Off means captures simply wait in the inbox."
        on={settings.triage_captures}
        onChange={(on) => void save({ triage_captures: on })}
        testId="triage-captures"
      />
      <Field
        label="Offer to sort the inbox when it reaches"
        hint="How many things may pile up before the assistant proposes where each one goes. 0 never offers."
      >
        <input
          type="number"
          min={0}
          max={100}
          defaultValue={settings.triage_inbox_at}
          key={`tri-${settings.triage_inbox_at}`}
          onBlur={(e) => {
            const v = Number(e.target.value)
            if (Number.isFinite(v) && v >= 0 && v <= 100) void save({ triage_inbox_at: v })
          }}
          className="w-24"
          data-testid="triage-inbox-at"
        />
      </Field>
      {copied && copied.startsWith('Could') && <p className="text-xs text-rose-300 mt-1">{copied}</p>}
    </section>
  )
}

/** Prime and agents: Prime on Claude Code, how far it acts, and what the coding agents may spend. */
const PrimeAgentsSection = ({
  settings,
  save,
}: {
  settings: SettingsSpec
  save: (patch: Partial<SettingsSpec>) => Promise<void>
}) => (
  <section className="mb-6" data-testid="agents-section">
    <LeadControl />
    <SwitchRow
      className="mt-4"
      label="Prime looks after the coding agents"
      hint="When a coding agent finishes a turn or stops, Prime reviews what it did (its thread and its pull request), tells it what to fix or answers its question, and sends you the gist. Starting an agent and merging still wait for your tap. It hands back to you after six rounds with one agent without a word from you."
      on={settings.prime_watches_agents !== false}
      onChange={(on) => void save({ prime_watches_agents: on })}
      testId="prime-watches-agents"
    />
    <SwitchRow
      className="mt-2"
      label="Let the agent do it, not just suggest it"
      hint="In an agent session, what it proposes happens straight away and the turn shows what it did with Undo beside it. The changes are made by this device, so ⌘Z and the Trash cover them like anything you did yourself. Off means every proposal waits for you to accept it."
      on={settings.agent_acts}
      onChange={(on) => void save({ agent_acts: on })}
      testId="agent-acts"
    />
    <div className="mt-4" />
    <Field
      label="Agent budget a day (USD)"
      hint="On a prototype Prime starts coding agents itself. Once the agents have cost this much today, its starts wait for your tap until midnight. Agents you start are never stopped. 0 is no cap."
    >
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={5}
        defaultValue={settings.agent_daily_usd ?? AGENT_DAILY_USD}
        key={`agent-budget-${settings.agent_daily_usd ?? AGENT_DAILY_USD}`}
        onBlur={(e) => {
          const v = Number(e.target.value)
          if (Number.isFinite(v) && v >= 0) void save({ agent_daily_usd: Math.round(v * 100) / 100 })
        }}
        className="w-32"
        data-testid="agent-budget"
      />
    </Field>
  </section>
)

/** Home-screen icon: a few marks, any accent, three backgrounds; drawn on the device. */
const AppIconSection = ({
  settings,
  save,
}: {
  settings: SettingsSpec
  save: (p: Partial<SettingsSpec>) => Promise<void>
}) => {
  const icon: AppIcon = (settings.app_icon as AppIcon | undefined) ?? DEFAULT_APP_ICON
  const set = (patch: Partial<AppIcon>) => void save({ app_icon: { ...icon, ...patch } })
  const previews = useMemo(
    () => ICON_STYLES.map((style) => ({ style, src: renderAppIcon({ ...icon, style }, 96) })),
    [icon],
  )
  const bgs = useMemo(() => ICON_BGS.map((bg) => ({ bg, src: renderAppIcon({ ...icon, bg }, 64) })), [icon])
  return (
    <section className="mb-6" data-testid="app-icon-section">
      <h2 className="text-sm font-medium mb-2">App icon</h2>
      <div className="flex items-center gap-4 mb-3">
        <img
          src={renderAppIcon(icon, 128)}
          alt="Current app icon"
          className="h-16 w-16 rounded-[14px] shadow-[var(--shadow-pop)]"
          data-testid="app-icon-preview"
        />
        <p className="text-xs text-faint">
          Used when you add AlfieOS to a home screen. An installed app keeps the icon it was installed with,
          so reinstall to change it.
        </p>
      </div>
      <Field label="Mark">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Icon mark">
          {previews.map((p) => (
            <button
              key={p.style}
              type="button"
              role="radio"
              aria-checked={icon.style === p.style}
              onClick={() => set({ style: p.style })}
              className={`rounded-xl p-1 border ${icon.style === p.style ? 'border-accent' : 'border-transparent hover:border-line-strong'}`}
              data-testid={`app-icon-${p.style}`}
            >
              <img src={p.src} alt={p.style} className="h-12 w-12 rounded-[10px]" />
            </button>
          ))}
        </div>
      </Field>
      <Field label="Colour">
        <ColourPicker value={icon.colour as Hue} onChange={(c) => set({ colour: c })} />
      </Field>
      <Field label="Background">
        <div className="flex gap-2" role="radiogroup" aria-label="Icon background">
          {bgs.map((b) => (
            <button
              key={b.bg}
              type="button"
              role="radio"
              aria-checked={icon.bg === b.bg}
              onClick={() => set({ bg: b.bg })}
              className={`rounded-lg p-1 border text-xs flex flex-col items-center gap-1 ${icon.bg === b.bg ? 'border-accent' : 'border-transparent hover:border-line-strong'}`}
            >
              <img src={b.src} alt={b.bg} className="h-8 w-8 rounded-md" />
              <span className="text-muted">{b.bg.charAt(0).toUpperCase() + b.bg.slice(1)}</span>
            </button>
          ))}
        </div>
      </Field>
    </section>
  )
}

const LabelStylesSection = ({
  settings,
  save,
}: {
  settings: SettingsSpec
  save: (p: Partial<SettingsSpec>) => Promise<void>
}) => {
  const tasks = useAllTasks() ?? []
  const values = new Map<string, number>()
  for (const t of tasks)
    for (const [k, v] of Object.entries(t.labels))
      if (!hiddenLabel(k)) values.set(`${k}:${v}`, (values.get(`${k}:${v}`) ?? 0) + 1)
  const keys = [...values.keys()].sort()
  const [open, setOpen] = useState<string | null>(null)
  const styles = settings.label_styles ?? {}
  const update = (kv: string, patch: { colour?: string; icon?: string }) =>
    save({ label_styles: { ...styles, [kv]: { ...(styles[kv] ?? {}), ...patch } } })
  if (!keys.length) return null
  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium mb-2">Label colours</h2>
      <p className="text-xs text-faint mb-2">
        Every label value gets a stable colour automatically. Tap one to choose your own colour or icon.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {keys.map((kv) => {
          const [k, v] = kv.split(':') as [string, string]
          const st = labelStyle(k, v, styles)
          return (
            <button
              key={kv}
              type="button"
              onClick={() => setOpen(open === kv ? null : kv)}
              className="min-h-8"
            >
              <Tag style={st.style} title={kv}>
                {st.icon ? `${st.icon} ` : ''}
                {labelWords(k, v)}
              </Tag>
            </button>
          )
        })}
      </div>
      {open && (
        <div className="mt-3 rounded-lg bg-panel p-3 fade-in">
          <div className="flex items-center gap-3 mb-3">
            <IconButtonField
              value={styles[open]?.icon}
              onChange={(icon) => void update(open, { icon: icon ?? '' })}
            />
            <span className="text-sm">{labelWords(...(open.split(':') as [string, string]))}</span>
          </div>
          <ColourPicker
            value={
              (styles[open]?.colour as Hue | undefined) ??
              labelStyle(...(open.split(':') as [string, string]), styles).hue
            }
            onChange={(c) => void update(open, { colour: c })}
          />
        </div>
      )}
    </section>
  )
}

type ProviderName = 'forgejo' | 'github' | 'factory'
const PROVIDERS: { name: ProviderName; title: string; hint: string }[] = [
  {
    name: 'forgejo',
    title: 'Forgejo',
    hint: 'git.alfiemills.co.uk; token and webhook secret come from the server environment',
  },
  {
    name: 'github',
    title: 'GitHub',
    hint: 'api.github.com; token and webhook secret come from the server environment',
  },
  {
    name: 'factory',
    title: 'Software factory',
    hint: 'Agents that take a task and come back with a pull request; URL, token and webhook secret come from the server environment',
  },
]

const ConnectorCard = ({
  provider,
  title,
  hint,
  existing,
  workspaces,
}: {
  provider: ProviderName
  title: string
  hint: string
  existing?: Resource
  workspaces: WorkspaceResource[]
}) => {
  const spec = (existing?.spec ?? {}) as Partial<ConnectorSpec>
  const status = (existing?.status ?? {}) as Partial<ConnectorStatus>
  const [enabled, setEnabled] = useState(spec.enabled ?? false)
  const [repos, setRepos] = useState((spec.repos ?? []).join('\n'))
  const [ws, setWs] = useState(spec.workspace ?? '')
  const [reviewTasks, setReviewTasks] = useState(spec.review_tasks ?? true)
  const [baseUrl, setBaseUrl] = useState(spec.base_url ?? '')
  const [msg, setMsg] = useState<string | null>(null)
  const isGit = provider === 'forgejo' || provider === 'github'
  const save = async () => {
    setMsg(null)
    const next: Record<string, unknown> = {
      provider,
      enabled,
      repos: isGit
        ? repos
            .split(/[\n,\s]+/)
            .map((r) => r.trim())
            .filter(Boolean)
        : [],
      review_tasks: reviewTasks,
      ...(ws ? { workspace: ws } : {}),
      ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
    }
    try {
      if (existing) {
        const patch: Record<string, unknown | null> = {}
        for (const key of ['enabled', 'repos', 'review_tasks', 'workspace', 'base_url'] as const) {
          if (JSON.stringify((existing.spec as Record<string, unknown>)[key]) !== JSON.stringify(next[key]))
            patch[key] = next[key] === undefined ? null : next[key]
        }
        if (Object.keys(patch).length) await mutate.patch(existing.id, { spec: patch })
      } else {
        await mutate.create('ConnectorConfig', next, {}, CONNECTOR_IDS[provider])
      }
      setMsg('Saved. The server polls every 10 minutes; webhooks arrive at once.')
    } catch (e) {
      setMsg(e instanceof KindError ? e.message : (e as Error).message)
    }
  }
  return (
    <div className="card p-3 mb-3" data-testid={`connector-${provider}`}>
      <SwitchRow
        className="mb-2"
        label={title}
        hint={hint}
        on={enabled}
        onChange={setEnabled}
        testId={`connector-${provider}-enabled`}
      />
      {isGit && (
        <Field label="Repositories" hint="owner/repo, one per line">
          <textarea
            value={repos}
            onChange={(e) => setRepos(e.target.value)}
            rows={3}
            className="w-full font-mono text-sm"
          />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Into suite">
          <select value={ws} onChange={(e) => setWs(e.target.value)} className="w-full">
            {/* no choice is the default suite: name it rather than say "default" */}
            <option value="">
              {workspaces.find((w) => w.spec.slug === DEFAULT_WORKSPACE_SLUG)?.spec.name ?? 'The first suite'}
            </option>
            {workspaces
              .filter((w) => w.spec.slug !== DEFAULT_WORKSPACE_SLUG)
              .map((w) => (
                <option key={w.id} value={w.spec.slug}>
                  {w.spec.name}
                </option>
              ))}
          </select>
        </Field>
        {provider !== 'factory' && (
          <Field label="API base (optional)">
            <input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="w-full font-mono text-sm"
              placeholder="default"
            />
          </Field>
        )}
      </div>
      {isGit && (
        <SwitchRow
          className="mb-3"
          label="Put review requests on Today as tasks"
          on={reviewTasks}
          onChange={setReviewTasks}
        />
      )}
      <div className="flex items-center justify-between gap-2">
        <Phrases
          className="text-xs text-faint"
          parts={[
            status.me && `As ${status.me}`,
            // the heartbeat is not synced (it would wake every device), so this
            // is the time of the last change, not of the last poll
            status.last_poll_at &&
              `Last change ${new Date(status.last_poll_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
            !!status.mirror_count && `${status.mirror_count} mirrored`,
            status.last_error,
          ]}
        />
        <Button variant="primary" onClick={() => void save()}>
          Save
        </Button>
      </div>
      {msg && <p className="text-xs text-muted mt-2">{msg}</p>}
    </div>
  )
}

/**
 * Spotify: approve once in the browser (PKCE, so the server needs only a
 * client id), then the server pulls history and playlists every ten minutes
 * and answers the player's poll. The refresh token lives in the server's
 * settings table and never reaches a device.
 */
/** The browser's recogniser beside the agent, for showing words live */
const InterimWordsSwitch = () => {
  const on = useVoicePrefs((s) => s.liveWords)
  const set = useVoicePrefs((s) => s.set)
  return (
    <SwitchRow
      label="Words as you say them"
      hint="Shows your words in the pill as you speak, from this device's own recogniser. On by default, except on a Mac, where sharing the microphone silences the agent's replies. If nothing shows, Activity has the reason under Voice."
      on={on}
      onChange={(v) => set({ liveWords: v })}
      testId="live-words"
    />
  )
}

const PushToTalkSwitch = () => {
  const on = useVoicePrefs((s) => s.pushToTalk)
  const set = useVoicePrefs((s) => s.set)
  return (
    <SwitchRow
      label="Push to talk"
      hint="The microphone stays shut between turns: hold the centre button to talk and let go for the reply, so nothing in the background reaches the agent. Off, the mic is open and a tap on the centre button says you are done."
      on={on}
      onChange={(v) => set({ pushToTalk: v })}
      testId="push-to-talk"
    />
  )
}

/** The spoken agent: whether the server has one, and the voice it speaks with. */
const VoiceSection = ({
  settings,
  save,
}: {
  settings: SettingsSpec
  save: (patch: Partial<SettingsSpec>) => Promise<void>
}) => {
  const [status, setStatus] = useState<{ configured: boolean; agent_id?: string; error?: string } | null>(
    null,
  )
  useEffect(() => {
    api
      .config()
      .then((c) => setStatus({ configured: !!c.voice }))
      .catch(() => setStatus({ configured: false }))
  }, [])
  if (status && !status.configured)
    return (
      <div className="flex flex-col gap-3">
        <SectionTitle>Spoken agent</SectionTitle>
        <p className="text-sm text-faint">
          Off. Put an ElevenLabs key on the server as <code>ALFIEOS_ELEVENLABS_KEY</code>, with{' '}
          <code>ALFIEOS_PUBLIC_URL</code> set to this host, and the agent, its tools and its callback are made
          for you.
        </p>
      </div>
    )
  return (
    <div className="flex flex-col gap-4">
      <div>
        <SectionTitle>Spoken agent</SectionTitle>
        <p className="text-sm text-faint">
          On. Talk from the mic beside Ask Prime on Home and in Apps. ElevenLabs listens and speaks; AlfieOS
          thinks. It captures, plays music, runs the timer, opens pages and makes the same changes chat can.
        </p>
      </div>
      <div>
        <InterimWordsSwitch />
        <PushToTalkSwitch />
      </div>
      <div>
        <SectionTitle>Voice</SectionTitle>
        <VoicePicker />
      </div>
      <div>
        <SectionTitle>What it remembers</SectionTitle>
        <MemoryList />
      </div>
      <div>
        <SectionTitle>Usage</SectionTitle>
        <Field
          label="Daily token budget"
          hint="All model calls together (fresh, cached and output tokens). Past it the assistant declines until midnight. 0 is no cap; 300000 is a busy day of talking."
        >
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={10000}
            defaultValue={settings.assistant_daily_tokens}
            key={`budget-${settings.assistant_daily_tokens}`}
            onBlur={(e) => {
              const v = Number(e.target.value)
              if (Number.isFinite(v) && v >= 0) void save({ assistant_daily_tokens: Math.round(v) })
            }}
            className="w-40"
            data-testid="assistant-budget"
          />
        </Field>
        <UsagePanel />
      </div>
    </div>
  )
}

const SpotifySection = () => {
  const [status, setStatus] = useState<SpotifyStatus | null>(null)
  const [msg, setMsg] = useState<string | null>(() => {
    const q = new URLSearchParams(window.location.search).get('spotify')
    return q === 'connected'
      ? 'Connected.'
      : q === 'refused'
        ? 'Spotify said you refused.'
        : q === 'failed'
          ? 'That approval did not go through. Try again.'
          : null
  })
  const [busy, setBusy] = useState(false)
  const load = () =>
    api
      .spotifyStatus()
      .then(setStatus)
      .catch(() => setStatus({ configured: false, connected: false }))
  useEffect(() => {
    void load()
  }, [])
  const connect = async () => {
    setBusy(true)
    try {
      const r = await api.spotifyConnect()
      window.location.href = r.url
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not start')
      setBusy(false)
    }
  }
  const disconnect = async () => {
    setBusy(true)
    try {
      await api.spotifyDisconnect()
      setMsg('Disconnected. The history already synced stays.')
      await load()
    } finally {
      setBusy(false)
    }
  }
  const [importNames, setImportNames] = useState('')
  const [lfmUser, setLfmUser] = useState('')
  const [lfmKey, setLfmKey] = useState('')
  const importLastFM = async () => {
    setBusy(true)
    try {
      await api.spotifyImportLastFM(lfmUser, lfmKey)
      setLfmKey('')
      setMsg('Last.fm import started. Progress shows below.')
      await load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not start the import')
    } finally {
      setBusy(false)
    }
  }
  const clearImport = async (source: 'lastfm' | 'spotify') => {
    setBusy(true)
    try {
      const r = await api.spotifyClearImport(source)
      setMsg(`Removed ${r.removed} plays. The live sync's plays stay.`)
      await load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not remove them')
    } finally {
      setBusy(false)
    }
  }
  // while a Last.fm import runs, keep the progress line moving
  const lfmRunning = !!status?.lastfm?.running
  useEffect(() => {
    if (!lfmRunning) return
    const i = setInterval(() => void load(), 2000)
    return () => clearInterval(i)
  }, [lfmRunning])
  const importFiles = async (files: File[]) => {
    if (!files.length) return
    setBusy(true)
    setMsg('Importing… a full history can take a minute.')
    try {
      const r = await api.spotifyImport(files)
      setMsg(
        `Imported ${r.plays} plays and ${r.tracks} new tracks from ${r.files} file${r.files === 1 ? '' : 's'}` +
          (r.already ? `, ${r.already} already held` : '') +
          (r.skipped ? `, ${r.skipped} too short to count` : '') +
          '.',
      )
      await load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Import failed')
    } finally {
      setBusy(false)
    }
  }
  const sync = async () => {
    setBusy(true)
    try {
      const r = await api.spotifySync()
      setMsg(`Synced: ${r.plays} plays, ${r.tracks} new tracks, ${r.playlists} playlists.`)
      await load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Sync failed')
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="mb-6" data-testid="spotify-section">
      <p className="text-xs text-faint mb-3">
        Recently played tracks and your playlists go into the library, listening patterns are read from them,
        and the player shows what is playing with play, pause and skip (Spotify allows control on Premium
        only).
      </p>
      {status === null ? null : !status.configured ? (
        <p className="text-sm text-muted" data-testid="spotify-unconfigured">
          Not configured on the server. Create an app at developer.spotify.com with the redirect{' '}
          <code className="text-xs">{window.location.origin}/spotify/callback</code> and set{' '}
          <code className="text-xs">ALFIEOS_SPOTIFY_CLIENT_ID</code>.
        </p>
      ) : (
        <div className="card p-3 flex flex-col gap-2">
          <div className="text-sm">
            {status.connected
              ? status.connected_at
                ? `Connected since ${new Date(status.connected_at).toLocaleDateString()}`
                : 'Connected'
              : 'Not connected'}
          </div>
          {status.sync && (
            <div className="text-xs text-faint">
              Last sync {new Date(status.sync.at).toLocaleString()}: {status.sync.plays} plays,{' '}
              {status.sync.tracks} new tracks, {status.sync.playlists} playlists.
              {status.sync.error ? ` It said: ${status.sync.error}` : ''}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {status.connected ? (
              <>
                <Button onClick={() => void sync()} disabled={busy}>
                  Sync now
                </Button>
                <Button variant="danger" onClick={() => void disconnect()} disabled={busy}>
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={() => void connect()}
                disabled={busy}
                data-testid="spotify-connect"
              >
                Connect Spotify
              </Button>
            )}
          </div>
        </div>
      )}
      {status?.configured && (
        <div className="mt-4">
          <h3 className="text-sm font-medium mb-1">Import your full history</h3>
          <p className="text-xs text-faint mb-2">
            Spotify's API hands over only the last 50 plays. For everything before that, ask Spotify for your
            data (Account → Privacy → Download your data; the extended streaming history takes up to 30 days)
            and drop the zip here as it arrived. Re-importing adds nothing twice.
          </p>
          {/* a card with the button inside: inner radius = card radius minus its padding, per the tokens */}
          <label className="card p-1.5 flex items-center gap-3 cursor-pointer rounded-[var(--radius-card)]">
            <span className="rounded-[var(--radius-card-inner)] bg-raised border border-line px-3 min-h-9 inline-flex items-center text-sm font-medium shrink-0">
              Choose files
            </span>
            <span className="text-sm text-muted truncate">{importNames || 'the zip, or its JSON files'}</span>
            <input
              type="file"
              accept=".zip,.json,application/zip,application/json"
              multiple
              disabled={busy}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                setImportNames(files.map((f) => f.name).join(', '))
                void importFiles(files)
              }}
              className="sr-only"
              data-testid="spotify-import"
            />
          </label>
          <p className="text-xs text-faint mt-4 mb-2">
            Or pull your Last.fm scrobbles once. Get a free API key at last.fm/api/account/create; the key is
            used for this import and kept nowhere. A scrobble that Spotify already holds is not counted again.
          </p>
          <form
            className="flex flex-wrap gap-2 items-end"
            onSubmit={(e) => {
              e.preventDefault()
              void importLastFM()
            }}
          >
            <Field label="Last.fm username">
              <input
                value={lfmUser}
                onChange={(e) => setLfmUser(e.target.value)}
                autoComplete="off"
                data-testid="lastfm-user"
              />
            </Field>
            <Field label="API key">
              <input
                value={lfmKey}
                onChange={(e) => setLfmKey(e.target.value)}
                autoComplete="off"
                data-testid="lastfm-key"
              />
            </Field>
            <Button
              type="submit"
              disabled={busy || !lfmUser.trim() || !lfmKey.trim() || !!status.lastfm?.running}
              className="mb-3"
              data-testid="lastfm-import"
            >
              Import from Last.fm
            </Button>
          </form>
          {status.lastfm && (
            <p className="text-xs text-faint" data-testid="lastfm-progress">
              {status.lastfm.running
                ? `Importing ${status.lastfm.user}: page ${status.lastfm.pages}${status.lastfm.total_pages ? ` of ${status.lastfm.total_pages}` : ''}, ${status.lastfm.plays} plays so far…`
                : status.lastfm.error
                  ? `Last.fm import stopped: ${status.lastfm.error}`
                  : `Last.fm import done: ${status.lastfm.plays} plays and ${status.lastfm.tracks} new tracks from ${status.lastfm.scrobbles} scrobbles${status.lastfm.already ? `, ${status.lastfm.already} already held` : ''}.`}
            </p>
          )}
          <div className="flex gap-2 mt-3">
            {status.lastfm && !status.lastfm.running && (
              <Button
                variant="chip"
                onClick={() => void clearImport('lastfm')}
                disabled={busy}
                data-testid="lastfm-clear"
              >
                Remove the Last.fm plays
              </Button>
            )}
            <Button variant="chip" onClick={() => void clearImport('spotify')} disabled={busy}>
              Remove the imported Spotify plays
            </Button>
          </div>
        </div>
      )}
      {msg && (
        <p className="text-sm text-fg-2 mt-3" role="status">
          {msg}
        </p>
      )}
    </section>
  )
}

const ConnectorsSection = () => {
  const configs = useConnectorConfigs()
  const workspaces = useWorkspaces().filter((w) => !w.status.archived_at)
  return (
    <section className="mb-6">
      <p className="text-xs text-faint mb-2">
        Mirrors issues and pull requests into a suite with the dev pack. Webhook URL:{' '}
        {typeof window !== 'undefined' ? window.location.origin : ''}/api/hooks/&lt;provider&gt;
      </p>
      {PROVIDERS.map((p) => (
        <ConnectorCard
          key={`${p.name}-${configs.find((c) => c.id === CONNECTOR_IDS[p.name])?.updated_at ?? 'new'}`}
          provider={p.name}
          title={p.title}
          hint={p.hint}
          existing={configs.find((c) => c.id === CONNECTOR_IDS[p.name])}
          workspaces={workspaces}
        />
      ))}
    </section>
  )
}

/** When a link or token was last used, in words. */
const usedLine = (g: GrantResource, unused = 'Never used'): string => {
  const live = isLive(g)
  if (!live.ok) return live.why ? live.why.charAt(0).toUpperCase() + live.why.slice(1) : 'Off'
  return g.status.last_used_at ? `Used ${new Date(g.status.last_used_at).toLocaleString()}` : unused
}

/** A suite's name for its slug, never the slug itself where a name is known. */
const useSuiteName = () => {
  const suites = useWorkspaces()
  return (slug: string) => suites.find((w) => w.spec.slug === slug)?.spec.name ?? slug
}

/** One row of links or tokens: name, the facts set apart, and Revoke while it is live. */
const GrantRow = ({
  g,
  parts,
  onOpen,
  openable,
  testId,
  revokeTestId,
}: {
  g: GrantResource
  parts: (string | false | null | undefined)[]
  onOpen?: () => void
  openable?: boolean
  testId?: string
  revokeTestId?: string
}) => {
  const live = isLive(g)
  const people = usePeople()
  return (
    <li
      className="flex items-center gap-3 px-3.5 min-h-14 py-2 text-sm"
      data-grant-id={g.id}
      data-live={live.ok ? 'yes' : 'no'}
    >
      <button
        type="button"
        className="min-w-0 flex-1 text-left"
        onClick={() => live.ok && onOpen?.()}
        disabled={!live.ok || !openable}
        data-testid={testId}
      >
        <span className={cx('block truncate text-[15px]', live.ok ? 'text-fg' : 'text-faint line-through')}>
          {g.spec.scopes.some((sc) => !!memberScope(sc)) ? memberNameWith(g.spec, people) : g.spec.name}
          {g.spec.paused ? <span className="text-faint"> (paused)</span> : null}
        </span>
        <Phrases className="text-xs text-faint mt-0.5" parts={parts} />
      </button>
      {live.ok && (
        <Button
          variant="danger"
          onClick={() => void mutate.action(g.id, 'revoke')}
          data-testid={revokeTestId}
        >
          Revoke
        </Button>
      )}
    </li>
  )
}

/**
 * The calendar feed: a secret link the iPhone subscribes to, so dated tasks,
 * reminders and deadlines show in Calendar and on the lock screen. The link is
 * a grant with the calendar scope; the secret is shown once and only its hash
 * is kept. Revoke it here or in Sharing.
 */
const CalendarSection = () => {
  const grants = useGrants().filter((g) => g.spec.scopes.includes('calendar'))
  const [made, setMade] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const make = async () => {
    setError(null)
    try {
      const secret = newSecret()
      await mutate.create(
        'Grant',
        { name: 'Calendar feed', scopes: ['calendar'], token_hash: await hashSecret(secret) },
        {},
      )
      setMade(`${location.host}/cal/${secret}.ics`)
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <section className="mb-6" data-testid="calendar-section">
      <p className="text-xs text-faint mb-3">
        Subscribe on your iPhone and every dated task, timed task, reminder and deadline appears in Calendar
        and on the lock screen, kept up to date by the phone. Tasks that slipped move to today. Nothing to
        import: the phone reads what is here each time it refreshes.
      </p>
      {made ? (
        <div className="card p-3.5 mb-3 flex flex-col gap-2" data-testid="calendar-made">
          <p className="text-xs text-muted">Shown once. Anyone with this link can read your calendar.</p>
          <code className="text-xs break-all text-fg-2" data-testid="calendar-url">
            https://{made}
          </code>
          <div className="flex flex-wrap gap-2">
            <a
              href={`webcal://${made}`}
              className="rounded-full bg-accent text-on-accent px-3.5 min-h-9 inline-flex items-center text-sm"
              data-testid="calendar-subscribe"
            >
              Subscribe on this iPhone
            </a>
            <Button
              variant="chip"
              onClick={() =>
                void navigator.clipboard
                  .writeText(`https://${made}`)
                  .then(() => setCopied(true))
                  .catch(() => {})
              }
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
          <p className="text-xs text-faint">
            Then in the iPhone's Settings → Calendar → Accounts → the AlfieOS calendar: turn Remove Alerts off
            so timed tasks and reminders alert, and set Fetch New Data to every 15 minutes.
          </p>
        </div>
      ) : (
        <Button variant="primary" onClick={() => void make()} className="mb-3" data-testid="calendar-make">
          {grants.some((g) => isLive(g).ok) ? 'Make another link' : 'Make a calendar link'}
        </Button>
      )}
      {error && <p className="text-xs text-amber-300/90 mb-2">{error}</p>}
      {grants.length > 0 && (
        <ul className="group-list" data-testid="calendar-grants">
          {grants.map((g) => (
            <GrantRow
              key={g.id}
              g={g}
              parts={[
                isLive(g).ok && g.status.last_used_at
                  ? `Last read ${new Date(g.status.last_used_at).toLocaleString()}`
                  : usedLine(g, 'Not read yet'),
              ]}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * API tokens (2026-09-29): keys for outside apps to reach /api/v1 with just
 * what they are given. Share links and the calendar feed live elsewhere.
 */
const TokensSection = () => {
  const tokens = useGrants().filter((g) => g.spec.api)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const edited = tokens.find((g) => g.id === editing)
  const suiteName = useSuiteName()
  return (
    <section className="mb-6" data-testid="tokens-section">
      <p className="text-xs text-faint mb-2">
        A key for an app, a bot or Claude Code, reaching only what you pick. Its changes wait for you unless
        you let an app's apply. Only a hash is kept here; the token is shown once.
      </p>
      {tokens.length > 0 && (
        <ul className="group-list mb-3" data-testid="tokens-list">
          {tokens.map((g) => {
            const reach = describeToken(g.spec.scopes, suiteName)
            return (
              <GrantRow
                key={g.id}
                g={g}
                onOpen={() => setEditing(g.id)}
                openable={!g.spec.scopes.some((sc) => !!memberScope(sc))}
                testId="token-edit"
                revokeTestId="token-revoke"
                parts={[
                  reach.charAt(0).toUpperCase() + reach.slice(1),
                  g.spec.writes === 'direct' ? 'Applies changes' : 'Changes wait for you',
                  g.spec.expires_at && `Until ${new Date(g.spec.expires_at).toLocaleDateString()}`,
                  usedLine(g),
                ]}
              />
            )
          })}
        </ul>
      )}
      <Button variant="primary" onClick={() => setCreating(true)} data-testid="token-new">
        + New token
      </Button>
      {creating && <TokenSheet onClose={() => setCreating(false)} />}
      {edited && <TokenSheet key={edited.id} token={edited} onClose={() => setEditing(null)} />}
    </section>
  )
}

const SharingSection = () => {
  const grants = useGrants().filter((g) => !g.spec.api)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const edited = grants.find((g) => g.id === editing)
  const suiteName = useSuiteName()
  const people = usePeople()
  // who a link's captures are from: the person's name for a handle, never the raw `from:` label
  const fromName = (handle: string) => {
    const p = personFor(people, handle)
    return p ? nameOf(p) : handle.charAt(0).toUpperCase() + handle.slice(1)
  }
  const describe = (scopes: string[], g?: GrantResource): string[] =>
    scopes.map((sc) => {
      if (sc === 'capture') return 'Capture only'
      if (sc === 'calendar') return 'Calendar feed'
      const m = memberScope(sc)
      if (m)
        return `Signs in to ${suiteName(m.slug)}${m.write ? ' and edits' : ', read only'}${g?.spec.admin ? ', admin' : ''}`
      const t = scopeTarget(sc)
      if (!t) return sc
      const kind = t.kind.charAt(0).toUpperCase() + t.kind.slice(1).toLowerCase()
      return `${kind}, ${t.write ? 'tick and add' : 'read only'}`
    })
  return (
    <section className="mb-6" data-testid="sharing-section">
      <p className="text-xs text-faint mb-2">
        Give someone a capture link (a page to send you things, or for their Shortcut), a link to one list,
        page or project, or an invitation to sign in to a whole suite. Each one is its own secret, so you can
        revoke it without touching yours. Only a hash is kept here; the secret is shown once.
      </p>
      {grants.length > 0 && (
        <ul className="group-list mb-3" data-testid="grants-list">
          {grants.map((g) => (
            <GrantRow
              key={g.id}
              g={g}
              onOpen={() => setEditing(g.id)}
              openable
              testId="grant-edit"
              revokeTestId="grant-revoke"
              parts={[
                ...describe(g.spec.scopes, g),
                !!g.spec.purpose && 'Capture page',
                g.spec.from && `From ${fromName(g.spec.from)}`,
                g.spec.max_uses === 1 && 'One-time',
                g.spec.expires_at && `Until ${new Date(g.spec.expires_at).toLocaleDateString()}`,
                usedLine(g),
              ]}
            />
          ))}
        </ul>
      )}
      <Button variant="primary" onClick={() => setCreating(true)} data-testid="share-new">
        + New share
      </Button>
      {creating && <ShareSheet onClose={() => setCreating(false)} />}
      {edited &&
        (edited.spec.scopes.some((sc) => !!memberScope(sc)) ? (
          <MemberSheet key={edited.id} grant={edited} onClose={() => setEditing(null)} />
        ) : (
          <GrantSheet key={edited.id} grant={edited} onClose={() => setEditing(null)} />
        ))}
    </section>
  )
}

/** Which build this device runs, which the server serves, and a manual update check. */
const AboutSection = () => {
  const [server, setServer] = useState<string | undefined>()
  const [ready, setReady] = useState(false)
  const [msg, setMsg] = useState('')
  useEffect(() => onSwUpdate(() => setReady(true)), [])
  useEffect(() => {
    api
      .config()
      .then((c) => setServer(c.version))
      .catch(() => setServer(undefined))
  }, [])
  const check = async () => {
    setMsg('Checking…')
    let serverVersion: string | undefined
    try {
      serverVersion = (await api.config()).version
      setServer(serverVersion)
    } catch {
      setMsg('Could not reach the server.')
      return
    }
    compareServerVersion(serverVersion)
    const r = await checkForUpdate(true)
    if (r === 'pending') setMsg('A new version is ready.')
    else if (r === 'unavailable') setMsg('Updates are handled by the browser here.')
    else if (serverVersion && serverVersion !== 'dev' && serverVersion !== APP_VERSION)
      setMsg('The server has a newer build; it is downloading now and will show up in a moment.')
    else setMsg("You're on the latest version.")
  }
  // a bare commit hash is cut to seven; a named build ("atlas-a1b2c3d", "dev") is shown whole
  const short = (v: string | undefined) => (!v ? '…' : /^[0-9a-f]{8,40}$/i.test(v) ? v.slice(0, 7) : v)
  return (
    <section className="mb-6" data-testid="about-section">
      <p className="text-xs text-muted mb-2">
        This device runs build <span className="font-mono text-fg">{short(APP_VERSION)}</span>
        {server && server !== APP_VERSION ? (
          <>
            , the server is on <span className="font-mono text-fg">{short(server)}</span>
          </>
        ) : (
          server && ', same as the server'
        )}
        .
      </p>
      <div className="flex flex-wrap gap-2">
        {ready ? (
          <Button variant="primary" onClick={() => void applySwUpdate()}>
            Update now
          </Button>
        ) : (
          <Button onClick={() => void check()} data-testid="check-updates">
            Check for updates
          </Button>
        )}
      </div>
      {msg && (
        <p className="text-xs text-muted mt-2" data-testid="update-msg">
          {msg}
        </p>
      )}
      <p className="text-xs text-faint mt-2">
        New builds are picked up when the app comes to the foreground and applied the next time it goes to the
        background, so you rarely need this.
      </p>
    </section>
  )
}

const SECTIONS = [
  { key: 'account', title: 'You', hint: 'Your name, notifications, signing out', Icon: UserRound },
  {
    key: 'appearance',
    title: 'Appearance',
    hint: 'Theme and accent; labels; app icon',
    Icon: SunMoon,
  },
  {
    key: 'home',
    title: 'Home screen',
    hint: 'Dock, wallpaper, icons, widgets, hidden apps',
    Icon: LayoutGrid,
  },
  { key: 'server', title: 'Server', hint: 'Your token and this device’s name', Icon: Server },
  // the route stays /settings/workspaces: a suite is a workspace in the data model
  {
    key: 'workspaces',
    title: 'Suites',
    hint: 'Add, edit or remove; each has its own apps and Home',
    Icon: Layers,
  },
  { key: 'connectors', title: 'Connectors', hint: 'Forgejo, GitHub, the factory', Icon: Plug },
  { key: 'spotify', title: 'Spotify', hint: 'Listening history, playlists, the player', Icon: Music },
  { key: 'voice', title: 'Voice', hint: 'The spoken agent and its voice', Icon: AudioLines },
  {
    key: 'agents',
    title: 'Prime and agents',
    hint: 'Prime on Claude Code, how far it acts, the agents’ budget',
    Icon: PrimeMark,
  },
  { key: 'capture', title: 'Quick capture', hint: 'The iPhone Shortcut; Prime’s suggestions', Icon: Mic },
  {
    key: 'sharing',
    title: 'Sharing',
    hint: 'Links and invitations you have made; revoke them',
    Icon: Share2,
  },
  {
    key: 'tokens',
    title: 'API tokens',
    hint: 'Keys for apps and bots, with just what you pick',
    Icon: KeyRound,
  },
  {
    key: 'calendar',
    title: 'Calendar',
    hint: 'Your tasks and reminders in the iPhone calendar',
    Icon: CalendarDays,
  },
  {
    key: 'notifications',
    title: 'Notifications',
    hint: 'Push, the morning brief and evening recap',
    Icon: Bell,
  },
  { key: 'dates', title: 'Dates', hint: 'How far ahead each kind of date reminds', Icon: CalendarHeart },
  {
    key: 'wellbeing',
    title: 'Wellbeing',
    hint: 'Check-in time, time anchors, the smoking unit, export',
    Icon: HeartPulse,
  },
  { key: 'less', title: 'Show less', hint: 'Caps and when things count as stale', Icon: EyeOff },
  { key: 'data', title: 'Data', hint: 'Export and reset this device', Icon: Database },
  { key: 'security', title: 'Security', hint: 'What protects this, devices, open doors', Icon: ShieldCheck },
  { key: 'usage', title: 'Usage', hint: 'Which features you use, and which you do not', Icon: BarChart3 },
  { key: 'about', title: 'About', hint: 'Build and updates', Icon: Info },
] as const

type SectionKey = (typeof SECTIONS)[number]['key']

/**
 * The hub's groups, in the order a person comes looking: how it looks and is
 * laid out, what shapes the day, the assistant, what the app talks to, then
 * the plumbing. Every section is in exactly one. (`/settings/look`, which held
 * label colours and the app icon, is Appearance now: `LEGACY_SECTION`.)
 */
const GROUPS: { label: string; keys: SectionKey[] }[] = [
  { label: 'Look and layout', keys: ['appearance', 'home', 'workspaces'] },
  { label: 'Your day', keys: ['notifications', 'dates', 'wellbeing', 'less'] },
  { label: 'Assistant', keys: ['agents', 'voice', 'capture'] },
  { label: 'Connected', keys: ['calendar', 'spotify', 'sharing', 'tokens', 'connectors'] },
  { label: 'This app', keys: ['server', 'data', 'usage', 'security', 'about'] },
]

/**
 * An admin member's Settings (2026-10-17): the sections that are about a person
 * and live on her own record or her own device. The owner's server, assistant,
 * sharing, API tokens, security, connectors and wellbeing are not hers (and her
 * token cannot reach them).
 */
const ADMIN_GROUPS: { label: string; keys: SectionKey[] }[] = [
  { label: 'You', keys: ['account'] },
  { label: 'Look and layout', keys: ['appearance', 'home'] },
  { label: 'Your day', keys: ['less'] },
  { label: 'This app', keys: ['about'] },
]

/** Old section routes that moved, so a bookmark or a stored link still lands. */
const LEGACY_SECTION: Record<string, SectionKey> = { look: 'appearance' }

const LEAD_CHOICES = [0, 1, 2, 3, 5, 7, 14, 30]
const leadWords = (n: number) =>
  n === 0
    ? 'On the day only'
    : n === 1
      ? 'The day before'
      : n === 7
        ? 'A week before'
        : n === 14
          ? 'Two weeks before'
          : n === 30
            ? 'A month before'
            : `${n} days before`

/** Lead days per kind of date; a date's own choice still wins over these. */
const DatesSection = ({
  leads,
  ready,
  save,
}: {
  leads?: Record<string, number>
  ready?: number
  save: (patch: Partial<SettingsSpec>) => Promise<void>
}) => (
  <section className="mb-6">
    <Field
      label="Appointments"
      hint="With a travel time: “get ready” this long before leaving, then “leave now”. Without one: a heads-up this long before it starts."
    >
      <Segmented
        label="Get ready"
        value={ready ?? 10}
        options={[
          { value: 0, label: 'None' },
          { value: 5, label: '5 min' },
          { value: 10, label: '10 min' },
          { value: 15, label: '15 min' },
          { value: 30, label: '30 min' },
        ]}
        onChange={(v) => void save({ ready_minutes: v })}
      />
    </Field>
    <p className="text-xs text-faint mb-3">
      Each date reminds you this far ahead, with the morning brief, and again on the day. A date can choose
      its own.
    </p>
    <ul className="group-list" data-testid="date-leads">
      {DATE_CATEGORIES.map((c) => {
        const Icon = CATEGORY_ICON[c]
        const cur = leads?.[c]
        return (
          <li key={c} className="flex items-center gap-3 px-3.5 min-h-14 py-2">
            <Icon size={18} className="shrink-0 text-muted" aria-hidden />
            <span className="flex-1 text-[15px]">{CATEGORY_LABEL[c]}</span>
            <select
              value={cur === undefined ? '' : String(cur)}
              onChange={(e) => {
                const next = { ...(leads ?? {}) }
                if (e.target.value === '') delete next[c]
                else next[c] = Number(e.target.value)
                void save({ date_leads: next })
              }}
              aria-label={`${CATEGORY_LABEL[c]}: how far ahead`}
              className="max-w-[11rem]"
              data-testid={`date-lead-${c}`}
            >
              <option value="">{leadWords(DEFAULT_LEADS[c])}</option>
              {LEAD_CHOICES.filter((n) => n !== DEFAULT_LEADS[c]).map((n) => (
                <option key={n} value={String(n)}>
                  {leadWords(n)}
                </option>
              ))}
            </select>
          </li>
        )
      })}
    </ul>
  </section>
)

/** The wellbeing CSV, from this device's own Day records, saved through the browser. */
const exportWellbeingCsv = async (unit: string) => {
  const days = (await db.resources.where('kind').equals('Day').toArray()) as DayResource[]
  const csv = toCsv(
    days.filter((d) => !d.deleted_at),
    unit,
  )
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `alfieos-wellbeing-${todayLocal()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Agent Prime on Claude Code: a long-running Claude Code session in the cluster
 * that reaches the app over MCP and leads the coding agents (docs/factory.md).
 */
const LeadControl = () => {
  const navigate = useNavigate()
  const [state, setState] = useState<{ run_id: string; live: boolean } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    api
      .lead()
      .then(setState)
      .catch(() => setState(null))
  }, [])
  const start = async () => {
    setBusy(true)
    setError('')
    try {
      const st = await api.leadStart()
      await engine.sync()
      void navigate({ to: '/agents/run/$id', params: { id: st.run_id } })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="text-sm" data-testid="lead-control">
      <p className="font-medium">Prime on Claude Code</p>
      <p className="text-xs text-faint mb-2">
        Prime as a long-running Claude Code session in the cluster: it reads your projects and notes, leads
        the coding agents, reviews their pull requests and checks staging and prod. Its changes still wait for
        your tap.
      </p>
      {state?.live ? (
        <Button
          variant="chip"
          onClick={() => void navigate({ to: '/agents/run/$id', params: { id: state.run_id } })}
        >
          Open Prime
        </Button>
      ) : (
        <Button variant="chipOn" onClick={() => void start()} disabled={busy} data-testid="lead-start">
          {busy ? 'Starting…' : 'Start Prime on Claude Code'}
        </Button>
      )}
      {error && <p className="text-xs text-rose-300 mt-1">{error}</p>}
    </div>
  )
}

const OwnerSettings = () => {
  const settings = useSettings()
  const token = useMeta<string>(META.token) ?? ''
  const deviceName = useMeta<string>(META.deviceName) ?? ''
  const deviceId = useMeta<string>(META.deviceId) ?? ''
  const subscribed = useMeta<boolean>(META.pushSubscribed) ?? false
  const [pushMsg, setPushMsg] = useState<string | null>(null)
  const [pushAvailable, setPushAvailable] = useState<boolean | null>(null)
  useEffect(() => {
    api
      .config()
      .then((c) => setPushAvailable(c.push_enabled))
      .catch(() => setPushAvailable(false))
  }, [])

  const save = async (patch: Partial<SettingsSpec>) => {
    await patchSettings(patch)
  }

  const num = (key: keyof SettingsSpec, min: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value)
    if (Number.isFinite(v) && v >= min) void save({ [key]: v })
  }

  const exportJson = async () => {
    const data = await api.export()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `alfieos-export-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const resetDevice = async () => {
    if (!confirm('Forget everything stored on this device and reconnect? Unsynced changes will be lost.'))
      return
    engine.stop()
    try {
      await unsubscribePush()
    } catch {
      /* best effort */
    }
    await db.delete()
    try {
      localStorage.removeItem('settings-cache')
    } catch {
      /* nothing kept */
    }
    window.location.replace('/')
  }

  const togglePush = async () => {
    setPushMsg(null)
    try {
      if (subscribed) {
        await unsubscribePush()
        setPushMsg('Notifications off on this device.')
      } else {
        await subscribePush()
        setPushMsg('Notifications on for this device.')
      }
    } catch (e) {
      setPushMsg((e as Error).message)
    }
  }

  const admin = useAccess((s) => s.admin)
  const groups = admin ? ADMIN_GROUPS : GROUPS
  const { section: asked } = useParams({ strict: false }) as { section?: string }
  const section = asked && asked in LEGACY_SECTION ? LEGACY_SECTION[asked] : asked
  const current = SECTIONS.find((sec) => sec.key === section && groups.some((g) => g.keys.includes(sec.key)))
  if (!current) {
    // the hub: one row per area, each its own page
    return (
      <div className="pb-20 md:pb-0" data-testid="settings-hub">
        <PageTitle>Settings</PageTitle>
        <div className="flex flex-col gap-5">
          {groups.map((g) => (
            <section key={g.label}>
              <h2 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">{g.label}</h2>
              <ul className="group-list">
                {g.keys.map((key) => {
                  const sec = SECTIONS.find((x) => x.key === key)!
                  return (
                    <li key={sec.key}>
                      <Link
                        to="/settings/$section"
                        params={{ section: sec.key }}
                        className="flex items-center gap-3 px-3.5 min-h-14 py-2 hover:bg-raised"
                        data-testid={`settings-${sec.key}`}
                      >
                        <span className="h-8 w-8 shrink-0 rounded-lg bg-raised text-fg-2 flex items-center justify-center">
                          <sec.Icon size={17} aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[15px] leading-5 text-fg">{sec.title}</span>
                          <span className="block text-xs text-faint truncate">{sec.hint}</span>
                        </span>
                        <ChevronRight size={16} className="text-faint shrink-0" aria-hidden />
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    )
  }
  return (
    <div className="pb-20 md:pb-0" data-testid={`settings-page-${current.key}`}>
      <PageTitle
        eyebrow={
          <Link to="/settings" className="hover:text-fg">
            ← Settings
          </Link>
        }
      >
        {current.title}
      </PageTitle>
      {section === 'server' && (
        <>
          <ServerSection
            key={`${token}|${deviceName}`}
            token={token}
            deviceName={deviceName}
            deviceId={deviceId}
          />
        </>
      )}
      {section === 'account' && <MemberAccount embedded />}
      {section === 'workspaces' && <SuitesSection />}
      {section === 'connectors' && (
        <>
          <ConnectorsSection />
        </>
      )}
      {section === 'capture' && (
        <>
          <QuickCaptureSection token={token} deviceId={deviceId} settings={settings} save={save} />
        </>
      )}
      {section === 'agents' && <PrimeAgentsSection settings={settings} save={save} />}
      {section === 'calendar' && <CalendarSection />}
      {section === 'home' && <HomeSettings />}
      {section === 'appearance' && (
        <AppearanceSettings>
          <LabelStylesSection settings={settings} save={save} />
          <AppIconSection settings={settings} save={save} />
          <Link to="/dev/design" className="text-sm text-accent-text">
            Design system gallery
          </Link>
        </AppearanceSettings>
      )}
      {section === 'sharing' && (
        <>
          <SharingSection />
        </>
      )}
      {section === 'tokens' && <TokensSection />}
      {section === 'notifications' && (
        <>
          <section className="mb-6">
            <p className="text-xs text-faint mb-2">
              On iPhone, notifications only work after adding AlfieOS to the Home Screen.
            </p>
            {pushAvailable === false && (
              <p className="text-xs text-faint mb-2">The server has no push keys configured.</p>
            )}
            <Button
              className="mr-2"
              onClick={() =>
                void api
                  .pushTest(deviceId ?? undefined)
                  .then(() => setPushMsg('Sent. It should arrive in a moment.'))
                  .catch((e: Error) => setPushMsg(e.message))
              }
              disabled={!subscribed || pushAvailable === false}
              data-testid="push-test"
            >
              Send a test notification
            </Button>
            <Button onClick={() => void togglePush()} disabled={!pushSupported() || pushAvailable === false}>
              {subscribed ? 'Turn off on this device' : 'Turn on for this device'}
            </Button>
            {pushMsg && <p className="text-xs text-muted mt-2">{pushMsg}</p>}
            <SwitchRow
              className="mt-4 mb-5"
              label="Tell my other devices when something new lands"
              hint="Captures, agent-made tasks, pages from the laptop."
              on={settings.notify_on_add}
              onChange={(on) => void save({ notify_on_add: on })}
              testId="notify-on-add"
            />
            <Field
              label="Morning brief at"
              hint="A push with what is lined up, by name. Opens the brief: I’m up, and a box to empty your head into. Leave empty for none."
            >
              <input
                type="time"
                defaultValue={settings.daily_push ?? ''}
                key={`dp-${settings.daily_push}`}
                onBlur={(e) => void save({ daily_push: e.target.value || null })}
                className="w-32"
                data-testid="morning-time"
              />
            </Field>
            <Field
              label="Evening recap at"
              hint="How the day went and a nudge towards what is left, by name. Opens the recap: done today, still on, Lights out. Leave empty for none."
            >
              <input
                type="time"
                defaultValue={settings.recap_push ?? ''}
                key={`rp-${settings.recap_push}`}
                onBlur={(e) => void save({ recap_push: e.target.value || null })}
                className="w-32"
                data-testid="recap-time"
              />
            </Field>
            <Field
              label="Plan the week, Sundays at"
              hint="What got done, loose ends, each day ahead and a box to empty your head into. Leave empty for none."
            >
              <input
                type="time"
                defaultValue={settings.week_push ?? ''}
                key={`wp-${settings.week_push}`}
                onBlur={(e) => void save({ week_push: e.target.value || null })}
                className="w-32"
                data-testid="week-time"
              />
            </Field>
            <Field
              label="Plan the weekend, Fridays at"
              hint="What is on, the house jobs that want doing, and what you want from it. Leave empty for none."
            >
              <input
                type="time"
                defaultValue={settings.weekend_push ?? ''}
                key={`wkp-${settings.weekend_push}`}
                onBlur={(e) => void save({ weekend_push: e.target.value || null })}
                className="w-32"
                data-testid="weekend-time"
              />
            </Field>
          </section>
        </>
      )}
      {section === 'dates' && (
        <DatesSection leads={settings.date_leads} ready={settings.ready_minutes} save={save} />
      )}
      {section === 'wellbeing' && (
        <>
          <section className="mb-6">
            <h2 className="text-sm font-medium mb-2">Evenings</h2>
            <p className="text-xs text-faint mb-3">
              The day ends when you press End of day, not at a time. These are the only clocks in it.
            </p>
            <Field
              label="Check-in"
              hint="One push a night, at this time, unless tonight's is already saved. Blank for none."
            >
              <input
                type="time"
                defaultValue={settings.checkin_push ?? ''}
                key={`cp-${settings.checkin_push}`}
                onBlur={(e) => void save({ checkin_push: e.target.value || null })}
                data-testid="checkin-time"
              />
            </Field>
            <Field
              label="Time anchors"
              hint="After End of day: the time, and how long since the chair. They stop at lights out."
            >
              <Segmented
                value={settings.anchor_minutes}
                options={[
                  { value: 0, label: 'Off' },
                  { value: 30, label: '30 min' },
                  { value: 45, label: '45 min' },
                  { value: 60, label: '1 h' },
                  { value: 90, label: '90 min' },
                ]}
                onChange={(v) => void save({ anchor_minutes: v })}
                label="Time anchors"
              />
            </Field>
            <Field label="Smoking unit" hint="What one tap of Smoke counts as, in your words.">
              <input
                defaultValue={settings.smoke_unit}
                key={`su-${settings.smoke_unit}`}
                maxLength={40}
                onBlur={(e) => void save({ smoke_unit: e.target.value.trim() || 'smoke' })}
                data-testid="smoke-unit"
              />
            </Field>
          </section>
          <section className="mb-6">
            <h2 className="text-sm font-medium mb-2">Your data</h2>
            <p className="text-xs text-faint mb-3">
              Every day's record lives on your devices and your server, and no share link or agent can reach
              it. The export is built here from this device's copy.
            </p>
            <Button
              onClick={() => void exportWellbeingCsv(settings.smoke_unit)}
              data-testid="wellbeing-export"
            >
              Export as CSV
            </Button>
          </section>
        </>
      )}
      {section === 'less' && (
        <>
          <section className="mb-6">
            <Field label="Today shows at most" hint="More is always one tap away.">
              <input
                type="number"
                min={1}
                max={20}
                defaultValue={settings.today_cap}
                key={`cap-${settings.today_cap}`}
                onBlur={num('today_cap', 1)}
                className="w-24"
              />
            </Field>
            <Field label="Suggest splitting tasks longer than (minutes)">
              <input
                type="number"
                min={1}
                defaultValue={settings.split_threshold}
                key={`split-${settings.split_threshold}`}
                onBlur={num('split_threshold', 1)}
                className="w-24"
              />
            </Field>
            <Field label="Inbox counts as stale after (days)">
              <input
                type="number"
                min={1}
                defaultValue={settings.stale_inbox_days}
                key={`si-${settings.stale_inbox_days}`}
                onBlur={num('stale_inbox_days', 1)}
                className="w-24"
              />
            </Field>
            <Field label="Unscheduled next counts as stale after (days)">
              <input
                type="number"
                min={1}
                defaultValue={settings.stale_next_days}
                key={`sn-${settings.stale_next_days}`}
                onBlur={num('stale_next_days', 1)}
                className="w-24"
              />
            </Field>
            <SwitchRow
              label="Show the weekly review hint on Today"
              on={settings.review_hint}
              onChange={(on) => void save({ review_hint: on })}
              testId="review-hint"
            />
          </section>
        </>
      )}
      {section === 'data' && (
        <>
          <section className="mb-6">
            <Button onClick={() => void exportJson()}>Export everything (JSON)</Button>
            <div className="mt-6" />
            <Button variant="danger" onClick={() => void resetDevice()}>
              Reset this device
            </Button>
            <p className="text-xs text-faint mt-1">
              Clears the local copy and returns to the token screen. Use after the server has been wiped or
              moved.
            </p>
          </section>
        </>
      )}
      {section === 'security' && <SecurityPanel settings={settings} save={save} />}
      {section === 'usage' && <FeatureUsage />}
      {section === 'spotify' && <SpotifySection />}
      {section === 'voice' && <VoiceSection settings={settings} save={save} />}
      {section === 'about' && (
        <>
          <AboutSection />
        </>
      )}
    </div>
  )
}

/** The owner's Settings, or a workspace member's short one (who they are, and signing out). */
export const SettingsView = () => {
  const member = useAccess((s) => s.member)
  const admin = useAccess((s) => s.admin)
  return member && !admin ? <MemberAccount /> : <OwnerSettings />
}
